import React, { useEffect, useState } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { CheckCircle2, Circle } from "lucide-react";
import api from "../lib/apiClient";
import { PageHeader, Card, Loading } from "../components/kit";

const fmtDate = (d) => d ? new Date(d + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "";

function Trend({ title, data, dataKey, invert }) {
  const vals = (data || []).map((d) => d[dataKey]).filter((v) => v != null);
  if (vals.length === 0) return null;
  const first = vals[0], last = vals[vals.length - 1];
  const delta = last - first;
  const good = invert ? delta <= 0 : delta >= 0;
  return (
    <Card className="p-5">
      <div className="flex items-center justify-between mb-1">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3] truncate pr-2">{title}</div>
        {vals.length > 1 && <span className={`text-xs font-bold shrink-0 ${good ? "text-[#CCFF00]" : "text-[#FF3B30]"}`}>{delta >= 0 ? "+" : ""}{delta.toFixed(delta % 1 ? 1 : 0)}</span>}
      </div>
      <div className="font-heading text-3xl mb-3">{last}</div>
      <ResponsiveContainer width="100%" height={110}>
        <LineChart data={data} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
          <CartesianGrid stroke="#1a1a1a" vertical={false} />
          <XAxis dataKey="label" tick={{ fill: "#666", fontSize: 10 }} axisLine={{ stroke: "#262626" }} tickLine={false} />
          <YAxis tick={{ fill: "#666", fontSize: 10 }} axisLine={false} tickLine={false} width={30} />
          <Tooltip contentStyle={{ background: "#141414", border: "1px solid #262626", fontSize: 12 }} labelStyle={{ color: "#CCFF00" }} />
          <Line type="monotone" dataKey={dataKey} stroke="#CCFF00" strokeWidth={2} dot={{ r: 3, fill: "#fff" }} />
        </LineChart>
      </ResponsiveContainer>
    </Card>
  );
}

function DrillStat({ drill, scores, completion }) {
  if (scores && scores.length > 0) {
    return <Trend title={drill.name} data={scores.map((d, i) => ({ ...d, label: `#${i + 1}` }))} dataKey="score" />;
  }
  const done = completion && completion.count > 0;
  return (
    <Card className="p-5" data-testid={`drill-stat-${drill.name}`}>
      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#A3A3A3] mb-1">{drill.skill}</div>
      <div className="font-heading text-lg uppercase mb-3 leading-tight">{drill.name}</div>
      {done ? (
        <div className="flex items-center gap-2 text-[#CCFF00] text-sm font-bold"><CheckCircle2 size={16} /> {completion.count}× completed</div>
      ) : (
        <div className="flex items-center gap-2 text-[#666] text-sm"><Circle size={14} /> Introduced · not logged yet</div>
      )}
      {done && completion.last_date && <div className="text-[10px] text-[#666] mt-1">Last: {completion.last_date}</div>}
      {drill.scoreable && !done && <div className="text-[10px] text-[#666] mt-2">Scoreable — log a score in Today's Training</div>}
    </Card>
  );
}

export default function Progress() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/progress").then((r) => setData(r.data)).catch(() => setData({ rounds: [], drills: [], catalog: [], completions: {} })); }, []);
  if (!data) return <div className="p-6"><Loading /></div>;

  const rounds = (data.rounds || []).map((r) => ({ ...r, label: fmtDate(r.played_date) }));
  const completions = data.completions || {};
  const scoresByDrill = {};
  (data.drills || []).forEach((d) => { (scoresByDrill[d.drill_name] ||= []).push(d); });

  const catalog = [...(data.catalog || [])];
  const catNames = new Set(catalog.map((c) => c.name));
  Object.keys(scoresByDrill).forEach((n) => { if (!catNames.has(n)) catalog.push({ name: n, category: "golf", skill: "Golf", scoreable: true }); });

  const inCat = (c) => catalog.filter((x) => x.category === c);
  const Section = ({ title, items }) => items.length === 0 ? null : (
    <>
      <h2 className="font-heading text-2xl uppercase mt-10 mb-4">{title}</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((d) => <DrillStat key={d.name} drill={d} scores={scoresByDrill[d.name]} completion={completions[d.name]} />)}
      </div>
    </>
  );

  const nothing = rounds.length === 0 && catalog.length === 0;

  return (
    <div className="p-5 sm:p-8 max-w-5xl mx-auto">
      <PageHeader eyebrow="Analytics" title="Progress" />
      {nothing ? (
        <Card className="p-8 text-center"><p className="text-[#A3A3A3]">Log rounds and complete training drills to see your trends here.</p></Card>
      ) : (
        <>
          {rounds.length > 0 && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <Trend title="Scoring Average" data={rounds} dataKey="score" invert />
              <Trend title="Putts / Round" data={rounds} dataKey="putts" invert />
              <Trend title="3-Putts" data={rounds} dataKey="three_putts" invert />
              <Trend title="GIR" data={rounds} dataKey="gir" />
              <Trend title="Fairways" data={rounds} dataKey="fairways_hit" />
              <Trend title="Penalties" data={rounds} dataKey="penalties" invert />
            </div>
          )}
          <Section title="Golf Drills" items={inCat("golf")} />
          <Section title="Mobility" items={inCat("mobility")} />
          <Section title="Strength" items={inCat("strength")} />
          <Section title="Tournament Warm-up" items={inCat("tournament")} />
        </>
      )}
    </div>
  );
}
